import React from "react";
import { RouteObject, Outlet, Navigate } from "react-router-dom";
import { AuthProvider } from "../auth/AuthProvider";
import { POSProvider } from "./providers/POSProvider";
import { AuthGuard } from "./components/AuthGuard";
import { POSLayout } from "./components/Layout/POSLayout";
import { DashboardPage } from "./pages/Dashboard/DashboardPage";
import { OrdersPage } from "./pages/Orders/OrdersPage";
import { SettingsPage } from "./pages/Settings/SettingsPage";
import { MenuPage } from "./pages/Menu/MenuPage";
import { CheckoutConfirmationPage } from "./pages/Checkout/CheckoutConfirmationPage";
import { DayBookPage } from "./pages/DayBook/DayBookPage";
import { SuccessfulPaymentPage } from "./pages/payments/SuccessPage"
import { TablesPage } from "./pages/Tables/TablesPage";
import { ReservationPage } from "./pages/Reservation/ReservationPage";
import { WaiterDashboard } from "./WaiterPages/WaiterDashboard/WaiterDashboard";
import { WaiterOrderScreen } from "./WaiterPages/OrderPage/OrderPage";

// AuthenticatedLayout ensures AuthProvider is available for all POS related components
const AuthenticatedLayout: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return <AuthProvider>{children}</AuthProvider>;
};

const POSPageLayout: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <AuthenticatedLayout>
      <AuthGuard>
        <POSProvider>
          <POSLayout>
            <React.Suspense fallback={<div>Loading...</div>}>
              {children}
            </React.Suspense>
          </POSLayout>
        </POSProvider>
      </AuthGuard>
    </AuthenticatedLayout>
  );
};

/**
 * POS Routes with a clear authentication and session management flow:
 *
 * 1. AuthenticatedLayout - Provides auth context
 * 2. AuthGuard - Verifies user is authenticated and has POS access permissions
 * 3. POSProvider - Provides POS service and state management
 * 4. POSLayout - Provides POS layout and routing
 * 5. POS - The main POS interface component
 */
export const posRoutes: RouteObject[] = [
  {
    path: "pos",
    element: <Outlet />,
    children: [
      {
        path: "",
        element: <Navigate to="orders" replace />,
      },
      {
        element: (
          <POSPageLayout>
            <Outlet />
          </POSPageLayout>
        ),
        children: [
          {
            path: "dashboard",
            element: <DashboardPage />,
          },
          {
            path: "orders",
            element: <OrdersPage />,
          },
          {
            path: "menu",
            element: <MenuPage />,
          },
          {
            path: "checkout",
            element: <CheckoutConfirmationPage />,
          },
          {
            path: "checkout/:orderId",
            element: <CheckoutConfirmationPage />,
          },
          {
            path: "daybook",
            element: <DayBookPage />,
          },
          {
            path: "waiterDashboard",
            element: <WaiterDashboard />,
          },
          {
            path: "waiterOrder",
            element: <WaiterOrderScreen />,
          },
        ],
      },
      {
        path: "success",
        element: <SuccessfulPaymentPage />,
      },
    ],
  },
];
